import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 5%;
  position: relative;
  z-index: 100;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
  }
`;

const Logo = styled(RouterLink)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;

  .material-icons-round {
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const MenuContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  transition: all 0.3s ease-in-out;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: rgba(10, 10, 15, 0.98);
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    transform: translateY(${props => props.isOpen ? '0' : '-100%'});
    opacity: ${props => props.isOpen ? '1' : '0'};
    pointer-events: ${props => props.isOpen ? 'all' : 'none'};
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    z-index: 99;
  }
`;

const MenuLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.2s;
  position: relative;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${props => props.delay};
  cursor: pointer;
  padding: 0.5rem 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: rgba(255, 255, 255, 0.2);
    transition: all 0.2s;
  }
  
  &:hover {
    color: white;
    
    &::after {
      width: 100%;
      background: rgba(255, 255, 255, 0.4);
    }
  }

  @media (max-width: 768px) {
    opacity: ${props => props.isOpen ? '1' : '0'};
    animation: ${props => props.isOpen ? fadeIn : 'none'} 0.3s ease-out forwards;
    animation-delay: ${props => props.delay};
    font-size: 1.1rem;
    padding: 0.75rem 0;
    width: 100%;
    text-align: center;
  }
`;



const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-left: 1rem;

  @media (max-width: 768px) {
    margin: 1rem 0 0 0;
    width: 100%;
    justify-content: center;
    opacity: ${props => props.isOpen ? '1' : '0'};
    animation: ${props => props.isOpen ? fadeIn : 'none'} 0.3s ease-out forwards;
    animation-delay: 0.4s;
  }
`;

const SignInButton = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
  padding: 0.4rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  transition: all 0.2s;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: 0.4s;
  background: rgba(255, 255, 255, 0.02);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.05);
    color: white;
    transform: translateY(-1px);
  }
`;



const scrollToSection = (e, sectionId) => {
  e.preventDefault();
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
    // Update URL without adding to history
    window.history.pushState(null, '', `#${sectionId}`);
  }
};

const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  div {
    width: 100%;
    height: 2px;
    background: white;
    border-radius: 2px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
    
    &:first-child {
      transform: ${({ isOpen }) => isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      opacity: ${({ isOpen }) => isOpen ? '0' : '1'};
      transform: ${({ isOpen }) => isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }
    
    &:nth-child(3) {
      transform: ${({ isOpen }) => isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleLinkClick = (e, sectionId) => {
    closeMenu();
    scrollToSection(e, sectionId);
  };

  return (
    <Nav style={scrolled ? { background: 'rgba(10, 10, 15, 0.98)' } : {}}>
      <Logo to="/" onClick={(e) => {
        e.preventDefault();
        closeMenu();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.history.pushState(null, '', '/');
      }}>
        <img src="/1.svg" alt="Surevest Logo" style={{ width: 120, height: 'auto', padding: '4px 0' }} />
      </Logo>
      
      <Hamburger isOpen={isOpen} onClick={toggleMenu}>
        <div />
        <div />
        <div />
      </Hamburger>
      
      <MenuContainer isOpen={isOpen}>
        <MenuLink 
          href="#services" 
          delay={isOpen ? "0.1s" : "0s"}
          onClick={(e) => handleLinkClick(e, 'services')}
          isOpen={isOpen}
        >
          Services
        </MenuLink>
        <MenuLink 
          href="#about" 
          delay={isOpen ? "0.2s" : "0s"}
          onClick={(e) => handleLinkClick(e, 'about')}
          isOpen={isOpen}
        >
          About
        </MenuLink>
        <MenuLink 
          href="#contact" 
          delay={isOpen ? "0.3s" : "0s"}
          onClick={(e) => handleLinkClick(e, 'contact')}
          isOpen={isOpen}
        >
          Contact
        </MenuLink>
        <ButtonGroup isOpen={isOpen}>
          <SignInButton 
            href="https://payroll.pss-group.co.za" 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            Sign In
          </SignInButton>
        </ButtonGroup>
      </MenuContainer>
    </Nav>
  );
};

export default Navbar;